import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  CircleDollarSign,
  Loader2,
  MessageSquare,
  Plus,
  RefreshCw,
  Search,
  Sparkles,
  Truck,
} from "lucide-react";
import { skipToken } from "@reduxjs/toolkit/query";
import { ReactNode, useCallback, useContext, useEffect, useId, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Progress } from "randmarcomps";
import { FormattedNumber } from "react-intl";
import {
  OrderDetail as ApiOrderDetail,
  useGetV4ManufacturerByRouteManufacturerIdAccountQuery,
  useGetV4ManufacturerByRouteManufacturerIdDocumentsActiveOrderDetailsQuery,
} from "randmar-api-client";
import OpenOrdersGrid from "../components/grids/OpenOrdersGrid";
import CurrencyUtils from "../components/util/CurrencyUtils";
import { DocumentHeader } from "../components/util/StyledComponents";
import ManufacturerContext from "../contexts/ManufacturerContext";
import Path from "../routes/Path";
import { OrderDetail } from "../stores/document/Document";
import { RootStoreContext } from "../stores/rootStore";
import withRoot from "../withRoot";

type OrderStatus = "processing" | "shipped" | "delayed";
type OrderStatusFilter = "all" | OrderStatus;

const getOrderStatus = (order: OrderDetail): OrderStatus => {
  if (order.documentType === "Shipment") {
    return "shipped";
  }
  if (order.documentType === "Order" && order.warehouseCode === "3PLE") {
    return "delayed";
  }
  return "processing";
};

const OrderDetails = () => {
  const { applicationId } = useContext(ManufacturerContext);
  const { warehousesStore } = useContext(RootStoreContext);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<OrderStatusFilter>("all");
  const [showOnlyBidOrders, setShowOnlyBidOrders] = useState(false);
  const headlineId = useId();
  const statusMessageId = useId();

  const queryArg = applicationId
    ? { routeManufacturerId: applicationId }
    : skipToken;

  const { data: manufacturerAccount } = useGetV4ManufacturerByRouteManufacturerIdAccountQuery(queryArg);

  const {
    data: apiOpenOrders,
    isLoading,
    isFetching,
    refetch,
  } = useGetV4ManufacturerByRouteManufacturerIdDocumentsActiveOrderDetailsQuery(queryArg);

  useEffect(() => {
    if (!isFetching && !isLoading && apiOpenOrders !== undefined) {
      setLastUpdated(new Date());
    }
  }, [apiOpenOrders, isFetching, isLoading]);

  const handleRefresh = useCallback(async () => {
    if (applicationId) {
      await refetch();
    }
  }, [applicationId, refetch]);

  const openOrders = useMemo(() => {
    if (!apiOpenOrders) {
      return [] as OrderDetail[];
    }
    return (apiOpenOrders as ApiOrderDetail[]).map(OrderDetail.convertFromApi);
  }, [apiOpenOrders]);

  const activeOrders = useMemo(() =>
    openOrders.slice()
      .sort((a, b) => (b.orderNumber || "").localeCompare(a.orderNumber || "")),
    [openOrders]
  );

  const hasOrders = activeOrders.length > 0;

  const loading = isLoading && !lastUpdated;

  const totals = useMemo(() => {
    return activeOrders.reduce(
      (acc, order) => {
        const amount = order.extendedPrice ?? 0;
        acc.totalValue += amount;

        if (order.documentType === "Shipment") {
          acc.shipped.count += 1;
          acc.shipped.value += amount;
          return acc;
        }

        if (order.documentType === "Order" && order.warehouseCode === "3PLE") {
          acc.delayed.count += 1;
          acc.delayed.value += amount;
          return acc;
        }

        acc.processing.count += 1;
        acc.processing.value += amount;
        return acc;
      },
      {
        processing: { count: 0, value: 0 },
        shipped: { count: 0, value: 0 },
        delayed: { count: 0, value: 0 },
        totalValue: 0,
      }
    );
  }, [activeOrders]);

  const lastUpdatedLabel = lastUpdated
    ? new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit" }).format(lastUpdated)
    : null;

  const normalizedSearch = searchTerm.trim().toLowerCase();

  const filteredOrders = useMemo(() => {
    if (!normalizedSearch && statusFilter === "all" && !showOnlyBidOrders) {
      return activeOrders;
    }
    return activeOrders.filter(order => {
      const matchesStatus = statusFilter === "all" || getOrderStatus(order) === statusFilter;
      const matchesBidFilter = !showOnlyBidOrders || Boolean(order.opportunityManufacturerNumber);

      if (!matchesStatus || !matchesBidFilter) {
        return false;
      }
      if (!normalizedSearch) {
        return true;
      }
      return [
        order.resellerName,
        order.orderNumber,
        order.poNumber,
        order.mpn,
        order.randmarSKU,
        warehousesStore.getWarehouseNameFromCode(order.warehouseCode),
      ].some(value => (value || "").toLowerCase().includes(normalizedSearch));
    });
  }, [activeOrders, normalizedSearch, showOnlyBidOrders, statusFilter, warehousesStore]);

  const filteredValue = useMemo(
    () => filteredOrders.reduce((sum, order) => sum + (order.extendedPrice ?? 0), 0),
    [filteredOrders]
  );

  const statusFilterOptions: { id: OrderStatusFilter; label: string; icon: ReactNode }[] = useMemo(
    () => [
      { id: "all", label: "All statuses", icon: <Sparkles className="h-4 w-4" /> },
      { id: "processing", label: "Processing", icon: <RefreshCw className="h-4 w-4" /> },
      { id: "shipped", label: "Shipped", icon: <Truck className="h-4 w-4" /> },
      { id: "delayed", label: "Delayed", icon: <AlertTriangle className="h-4 w-4" /> },
    ],
    []
  );

  const statusMessage = isFetching
    ? "Refreshing open orders..."
    : filteredOrders.length > 0
      ? `Showing ${filteredOrders.length} of ${activeOrders.length} open ${activeOrders.length === 1 ? "order" : "orders"} totaling ${CurrencyUtils.formatNumber(filteredValue)}.`
      : hasOrders
        ? "No orders match your search or filters."
        : "You are all caught up. We will surface new orders here automatically.";

  const metricCards = useMemo(() => ([
    {
      label: "Total Orders",
      count: activeOrders.length,
      amount: totals.totalValue,
      accent: "text-[var(--color-randmar-purple)]",
    },
    {
      label: "Processing Orders",
      count: totals.processing.count,
      amount: totals.processing.value,
      accent: "text-[var(--color-randmar-purple)]",
    },
    {
      label: "Shipped Orders",
      count: totals.shipped.count,
      amount: totals.shipped.value,
      accent: "text-[var(--color-randmar-green)]",
    },
    {
      label: "Delayed Orders",
      count: totals.delayed.count,
      amount: totals.delayed.value,
      accent: "text-[var(--color-randmar-yellow)]",
    },
  ]), [
    activeOrders.length,
    totals.delayed.count,
    totals.delayed.value,
    totals.processing.count,
    totals.processing.value,
    totals.shipped.count,
    totals.shipped.value,
    totals.totalValue,
  ]);

  if (!applicationId) {
    return <Progress />;
  }

  const marketingResourcesLink = manufacturerAccount?.PublicResourcesLink?.trim();
  const addResourcesLink = marketingResourcesLink && marketingResourcesLink.length > 0
    ? marketingResourcesLink
    : Path.resellers(applicationId);
  const isAddResourcesLinkExternal = /^https?:\/\//i.test(addResourcesLink);

  return (
    <main aria-busy={isFetching} aria-live="polite">
      <DocumentHeader
        sticky={false}
        direction="column"
        className="w-full gap-6 border border-[#d8c8f5] bg-gradient-to-r from-[#8E77C8]/20 via-[#f7f2ff] to-white xl:gap-8"
      >
        <div className="flex flex-col gap-4 text-[var(--color-randmar-purple)]">
          <div className="flex flex-wrap items-center gap-2 text-sm font-medium uppercase tracking-wide">
            <span className="categories-accent-pill inline-flex items-center gap-2 rounded-full px-3 py-1">
              <Sparkles className="h-4 w-4" aria-hidden />
              Manufacturer Portal
            </span>
            {isFetching && (
              <span className="inline-flex items-center gap-1 text-[var(--color-muted-foreground)]">
                <Loader2 className="h-4 w-4 animate-spin" />
                Syncing
              </span>
            )}
          </div>
          <div className="space-y-2 text-[var(--color-randmar-purple)]">
            <h1 id={headlineId} className="text-3xl font-semibold text-slate-900">
              Open Orders
            </h1>
            <p className="text-base text-[var(--color-muted-foreground)]">
              Monitor all orders from resellers going to the final destination.
            </p>
          </div>
        </div>

        <div className="grid gap-4 lg:gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
          <dl className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
            {metricCards.map(card => (
              <div
                key={card.label}
                className="flex flex-col gap-3 rounded-2xl border border-white/80 bg-white/90 p-5 shadow-[0_18px_45px_rgba(105,50,198,0.12)]"
              >
                <dt className={`text-xs font-semibold uppercase tracking-[0.25em] ${card.accent}`}>
                  {card.label}
                </dt>
                <dd className="text-4xl font-bold text-[var(--color-foreground)]">
                  {card.count}
                </dd>
                <dd className="text-sm font-medium text-[var(--color-muted-foreground)]">
                  <FormattedNumber value={card.amount} {...CurrencyUtils.format} />
                </dd>
              </div>
            ))}
          </dl>

          <div className="flex w-full max-w-sm flex-col gap-3 text-sm text-[var(--color-muted-foreground)] lg:w-auto lg:max-w-none">
            <div className="inline-flex w-full items-center gap-3 rounded-full border border-[#d7c8f3] bg-white px-4 py-2 text-sm font-semibold text-[#4e2c8c] shadow-[0_18px_45px_rgba(79,44,140,0.15)]">
              <span
                aria-hidden
                className={`h-2.5 w-2.5 rounded-full border border-white shadow-[0_0_0_4px_rgba(94,57,163,0.12)] ${isFetching ? "bg-[var(--color-randmar-yellow)]" : "bg-[var(--color-randmar-green)]"}`}
              />
              <span>{lastUpdatedLabel ? `Last updated at ${lastUpdatedLabel}` : "Syncing latest data"}</span>
            </div>
            <div className="flex w-full flex-col gap-3">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                aria-label="Refresh open orders"
                onClick={handleRefresh}
                disabled={loading || isFetching}
                className="w-full gap-2 rounded-full border border-[var(--color-randmar-purple)]/30 bg-white/80 text-[var(--color-randmar-purple)] shadow-sm transition hover:-translate-y-0.5 hover:border-[var(--color-randmar-purple)]/50 hover:bg-white"
              >
                <RefreshCw className={`h-4 w-4 transition-transform ${isFetching ? "animate-spin" : ""}`} />
                Refresh data
              </Button>

              <Button
                asChild
                size="sm"
                variant="purple"
                className="w-full rounded-full px-5 py-2 font-semibold text-white shadow-md transition hover:-translate-y-0.5"
              >
                <Link
                  to={Path.completedOrderDetails(applicationId)}
                  aria-label="View completed order history"
                  className="flex items-center justify-center gap-2 text-white"
                >
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/25">
                    <ArrowRight className="h-3 w-3 text-white" aria-hidden />
                  </span>
                  View Completed Orders
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </DocumentHeader>

      <section className="w-full rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search by reseller name, order number, reseller po, MPN, or location"
              className="w-full rounded-xl border-slate-200 bg-slate-50 pl-9 text-sm"
              aria-label="Search open orders"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold uppercase tracking-wide text-slate-500 hover:text-[color:var(--ops-accent)]"
              >
                Clear
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {statusFilterOptions.map(option => {
              const isActive = option.id === statusFilter;
              return (
                <button
                  type="button"
                  key={option.id}
                  aria-pressed={isActive}
                  onClick={() => setStatusFilter(option.id)}
                  className={`categories-filter-button inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium ${isActive ? "categories-filter-button--active" : ""}`}
                >
                  {option.icon}
                  {option.label}
                </button>
              );
            })}
            <button
              type="button"
              aria-pressed={showOnlyBidOrders}
              onClick={() => setShowOnlyBidOrders(prev => !prev)}
              className={`categories-filter-button inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium ${showOnlyBidOrders ? "categories-filter-button--active" : ""}`}
            >
              <CircleDollarSign className="h-4 w-4" />
              Bids
            </button>
          </div>
        </div>
        <p
          id={statusMessageId}
          role="status"
          className="mt-3 text-sm text-slate-500"
          aria-live="polite"
        >
          {statusMessage}
        </p>
      </section>

      {hasOrders ? (
        filteredOrders.length > 0 ? (
          <section
            aria-labelledby="orders-grid-title"
            className="relative mt-4 w-full space-y-6 overflow-hidden rounded-[32px] border border-[#e5dbff] bg-white/95 p-6 shadow-[0_45px_140px_-60px_rgba(63,25,102,0.8)]"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-3 pt-2">
              <div>
                <h2 id="orders-grid-title" className="text-xl font-semibold text-[var(--color-foreground)]">
                  Detailed timeline
                </h2>
                <p className="text-sm text-[var(--color-muted-foreground)]">
                  Sortable grid of every line item that is still open.
                </p>
              </div>
              <span className="inline-flex items-center gap-2 rounded-full bg-[#f6f1ff] px-4 py-1 text-xs font-semibold uppercase tracking-wide text-[#5c33a5] shadow-[0_18px_45px_-30px_rgba(73,32,118,0.8)]">
                <span className="h-2 w-2 rounded-full bg-[#a56bff]" aria-hidden />
                Updated {loading ? "just now" : "moments ago"}
              </span>
            </div>

            <div className="rounded-[28px] border border-[#efe4ff] bg-gradient-to-b from-white via-white to-[#f8f2ff] p-2 shadow-[0_35px_80px_-55px_rgba(66,24,107,0.85)]">
              <OpenOrdersGrid productOrders={filteredOrders} withHeader variant="timeline" />
            </div>
          </section>
        ) : (
          <Card className="rounded-2xl border border-dashed border-rose-200 bg-rose-50/60">
            <CardHeader>
              <CardTitle>No orders match what you’re looking for</CardTitle>
              <CardDescription>Update your filters or clear search to see every open order again.</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3 text-sm text-slate-600">
              <div className="inline-flex items-center gap-2 rounded-lg border border-rose-100 bg-white px-3 py-2">
                <AlertCircle className="h-4 w-4 text-rose-500" />
                Filters can be combined. Make sure you’re not limiting results unintentionally.
              </div>
              <Button
                onClick={() => {
                  setSearchTerm("");
                  setStatusFilter("all");
                  setShowOnlyBidOrders(false);
                }}
                className="self-start"
              >
                Reset filters
              </Button>
            </CardContent>
          </Card>
        )
      ) : (
        <section
          aria-labelledby="empty-state-title"
          className="flex flex-col items-center mt-4 gap-6 rounded-[var(--surface-radius-xl)] border border-dashed border-[#d8c8f5] bg-gradient-to-b from-white to-[#f7f0ff] px-6 py-12 text-center shadow-[var(--shadow-card)]"
        >
          <div className="categories-accent-pill inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide">
            <Sparkles className="h-4 w-4" aria-hidden />
            No reseller orders yet
          </div>

          <div className="space-y-2">
            <h2 id="empty-state-title" className="text-3xl font-semibold text-[var(--color-foreground)]">
              Provide marketing resources
            </h2>
            <p className="text-base text-[var(--color-muted-foreground)]">
              Launch with confidence by adding marketing resources. As soon as you add information, it becomes available to every reseller portal.
            </p>
            <p className="text-base text-[var(--color-muted-foreground)]">
              Chat with resellers and be Open to Work.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            <Button
              asChild
              size="sm"
              variant="purple"
              className="gap-2 px-6 py-4 text-base focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2"
            >
              {isAddResourcesLinkExternal ? (
                <a
                  href={addResourcesLink}
                  aria-label="Add reseller resources"
                  className="flex items-center gap-2"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Plus className="h-4 w-4" aria-hidden />
                  Add reseller resources
                </a>
              ) : (
                <Link
                  to={addResourcesLink}
                  aria-label="Add reseller resources"
                  className="flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" aria-hidden />
                  Add reseller resources
                </Link>
              )}
            </Button>
            <Button
              asChild
              size="sm"
              variant="outline"
              className="gap-2 px-6 py-4 text-base text-[var(--color-foreground)] focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2"
            >
              <Link
                to={Path.chat(applicationId)}
                aria-label="Chat with resellers"
                className="flex items-center gap-2"
              >
                <MessageSquare className="h-4 w-4" aria-hidden />
                Chat with resellers
              </Link>
            </Button>
          </div>
          <p className="text-xs text-[var(--color-muted-foreground)]">You can fine-tune automation and qualification later.</p>
        </section>
      )}
    </main>
  );
};

export default withRoot(OrderDetails);
